import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
  InputAdornment,
  Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate } from 'react-router-dom';
import { customerDirectoryService } from '../services/api';

const DEFAULT_PAYMENT_LABELS = {
  cash: 'Готівка',
  cashless: 'Безготівка',
  prepayment: 'Передплата',
};

function CustomersPage() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      const res = await customerDirectoryService.getAll();
      const rows = Array.isArray(res.data?.results) ? res.data.results : Array.isArray(res.data) ? res.data : [];
      setList(rows);
    } catch (e) {
      console.error(e);
      alert('Помилка завантаження замовників');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (c) =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.contact_full_name || '').toLowerCase().includes(q) ||
        (c.contact_phone || '').toLowerCase().includes(q) ||
        (c.contact_email || '').toLowerCase().includes(q) ||
        String(c.id).includes(q),
    );
  }, [list, search]);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">Замовники</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/customers/new')}>
          Додати замовника
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Довідник контрагентів: контактна особа, типові умови оплати та прайс-лист по виробах. У замовленні клієнта
        можна обрати запис з довідника.
      </Typography>
      <TextField
        size="small"
        placeholder="Пошук за назвою, контактною особою, телефоном, email…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2, minWidth: 320 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />
      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Код</TableCell>
                <TableCell>Назва замовника</TableCell>
                <TableCell>Контактна особа</TableCell>
                <TableCell>Телефон</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Типові умови оплати</TableCell>
                <TableCell align="center">Активний</TableCell>
                <TableCell>Примітка</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9}>
                    {list.length === 0 ? 'Немає замовників — натисніть «Додати».' : 'Нічого не знайдено.'}
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((c) => (
                  <TableRow key={c.id} hover>
                    <TableCell>{c.id}</TableCell>
                    <TableCell>{c.name}</TableCell>
                    <TableCell>{c.contact_full_name || '—'}</TableCell>
                    <TableCell>{c.contact_phone || '—'}</TableCell>
                    <TableCell>{c.contact_email || '—'}</TableCell>
                    <TableCell>{DEFAULT_PAYMENT_LABELS[c.default_payment_type] || '—'}</TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={c.is_active ? 'Так' : 'Ні'}
                        color={c.is_active ? 'success' : 'default'}
                        variant={c.is_active ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        maxWidth: 220,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={c.notes || ''}
                    >
                      {c.notes || '—'}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => navigate(`/customers/${c.id}`)}
                      >
                        Картка
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

export default CustomersPage;
